import { action } from '@ember/object';
import { didCancel, task, timeout } from 'ember-concurrency';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { waitForLater } from 'ember-simplepractice/utils/waiters';
import Component from '@glimmer/component';
import ContactListItem from './contact-list-item.gjs';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import MessagingPage from './messaging-page.gjs';
import SharedInfinityLoader from 'ember-simplepractice/components/shared/infinity-loader';
import TextInput from 'ember-simplepractice/components/spds/input/text';
import styles from './contacts-page.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export default class ContactsPage extends Component {
  @service messaging;
  @service alertManager;
  @service intl;

  @tracked search = '';
  @tracked isRecentContacts = true;

  get contactsList() {
    if (!this.messaging.contacts || this.messaging.getContacts.isRunning) {
      return [];
    }

    const contacts = this.messaging.contacts;

    if (this.search.trim()) {
      return contacts;
    }

    // If no search, show recent clients as default
    return contacts.filter(contact => contact.isRecent);
  }

  constructor() {
    super(...arguments);

    this.#initializeSearch();
  }

  async #initializeSearch() {
    // Defer the searchContacts call to the next tick to avoid assertion errors
    await waitForLater();
    this.searchContacts.perform();
  }

  @action
  leaveContactPage() {
    this.messaging.contactPageActive = false;
  }

  @action
  updateSearch(event) {
    this.search = event.target.value;
    this.searchContacts.perform();
  }

  @action
  clearSearch() {
    this.search = '';
    this.searchContacts.perform();
  }

  handleError(err) {
    if (didCancel(err)) return;
    this.alertManager.broadcast('toast', {
      message: this.intl.t('emc.general_error'),
      type: 'error',
    });
  }

  @action
  async loadMoreContacts(model) {
    try {
      await this.messaging.loadMoreContacts.perform(model);
    } catch (err) {
      this.handleError(err);
    }
  }

  searchContacts = task({ restartable: true }, async () => {
    const filter = this.search.trim() ? { search: this.search.trim() } : undefined;

    // Add debounce only for search input (not initial load)
    if (this.search) {
      await timeout(300);
    }

    try {
      await this.messaging.getContacts.perform(filter);
      this.isRecentContacts = !this.search;
    } catch (err) {
      this.handleError(err);
    }
  });

  <template>
    <MessagingPage class="{{styles.component}} contacts-page" data-test-contacts-page>
      <:header>
        <GhostButton
          @fitContent={{true}}
          @iconOnly={{true}}
          {{on "click" this.leaveContactPage}}
          class="back-button"
          data-test-contacts-page-back-button
        >
          {{svgJar "messaging-chevron-left"}}
        </GhostButton>
        <span data-test-contacts-page-title>{{t "emc.contacts_page.new_message"}}</span>
      </:header>
      <:default>
        <div class="search-container" data-test-search-container>
          <label for="contactSearchInput" class="search-label">{{t
              "emc.contacts_page.search_input_label"
            }}</label>
          <TextInput
            class="search-input"
            @id="contactSearchInput"
            @value={{this.search}}
            @onInput={{this.updateSearch}}
            @placeholder={{t "emc.contacts_page.search_placeholder"}}
            data-test-search-input
          />
          {{#if this.search}}
            <button
              type="button"
              class="clear-search-button"
              aria-label={{t "emc.contacts_page.clear_search"}}
              {{on "click" this.clearSearch}}
              data-test-clear-search-button
            >
              {{svgJar "x-mark"}}
            </button>
          {{/if}}
          <div id="search-help-text" class="visually-hidden">
            {{t "emc.contacts_page.search_help_text"}}
          </div>
        </div>
        {{#if this.messaging.getContacts.isRunning}}
          <div
            class="spinner"
            data-test-loading-spinner
            aria-live="polite"
            aria-label={{t "emc.contacts_page.loading"}}
          >
            {{svgJar "messaging-spinner"}}
          </div>
        {{else if this.contactsList}}
          {{#if this.isRecentContacts}}
            <div class="recent-conversations-label" data-test-recent-contacts-label>
              {{t "emc.contacts_page.recent_conversations"}}
            </div>
          {{/if}}
          <ul
            class="contact-list-body"
            data-test-contact-list-body
            role="listbox"
            aria-label={{t "emc.contacts_page.list"}}
          >
            {{#each this.contactsList as |contact|}}
              <ContactListItem @contact={{contact}} />
            {{/each}}
            {{#if this.search}}
              <SharedInfinityLoader
                @model={{this.messaging.contacts}}
                @infinityLoad={{this.loadMoreContacts}}
                aria-label={{t "emc.contacts_page.infinity_loader_label"}}
              />
            {{/if}}
          </ul>
        {{else}}
          <div
            class="empty-contact-list"
            data-test-empty-contact-list
            role="status"
            aria-live="polite"
            aria-label={{t "emc.contacts_page.no_contacts_found"}}
          >
          </div>
        {{/if}}
      </:default>
    </MessagingPage>
  </template>
}
